import React from "react";
import { Autocomplete, TextField, Chip, Typography, Box, Checkbox, Divider } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const SELECT_ALL_ID = "__select_all__";

/**
 * MultiSelect — inline chips + type-to-search multi-select.
 *
 * Props:
 *   options         [{ id, label }]
 *   value           string[]              selected ids
 *   onChange        (ids, options) => {}
 *   placeholder     string
 *   loading         bool
 *   disabled        bool
 *   width           string | number       (default "100%")
 *   noOptionsText   string
 *   enableBulkPaste bool                  parse comma-separated paste into selections
 *   showSelectAll   bool                  pin a Select All toggle at top of dropdown
 */
const MultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select…",
  loading = false,
  disabled = false,
  width = "100%",
  noOptionsText = "No options found",
  enableBulkPaste = false,
  showSelectAll = false,
}) => {
  const selectedIds = (value ?? []).map(String);
  const selectedOptions = options.filter((o) => selectedIds.includes(String(o.id)));
  const allSelected = options.length > 0 && selectedIds.length === options.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const augmentedOptions = showSelectAll
    ? [{ id: SELECT_ALL_ID, label: "Select All" }, ...options]
    : options;

  const handleChange = (_, newVal) => {
    if (showSelectAll && newVal.some((o) => o.id === SELECT_ALL_ID)) {
      // toggle: if all selected → clear, otherwise → select all
      if (allSelected) {
        onChange?.([], []);
      } else {
        onChange?.(options.map((o) => String(o.id)), options);
      }
      return;
    }
    const filtered = newVal.filter((o) => o.id !== SELECT_ALL_ID);
    onChange?.(filtered.map((o) => String(o.id)), filtered);
  };

  const handlePaste = (e) => {
    if (!enableBulkPaste) return;
    const text = e.clipboardData.getData("text");
    if (!text.includes(",")) return;
    e.preventDefault();
    const pasted = text.split(",").map((s) => s.trim()).filter(Boolean);
    const matched = options.filter((o) =>
      pasted.some((p) => String(o.id).toLowerCase() === p.toLowerCase() || o.label.toLowerCase() === p.toLowerCase())
    );
    if (matched.length === 0) return;
    const merged = [...selectedOptions, ...matched.filter((m) => !selectedIds.includes(String(m.id)))];
    onChange?.(merged.map((o) => String(o.id)), merged);
  };

  return (
    <Box sx={{ width }}>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={augmentedOptions}
        getOptionLabel={(o) => (o.id === SELECT_ALL_ID ? "" : (o.label ?? ""))}
        isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
        value={selectedOptions}
        onChange={handleChange}
        loading={loading}
        disabled={disabled}
        filterOptions={(opts, state) => {
          const filtered = opts.filter(
            (o) => o.id === SELECT_ALL_ID || o.label?.toLowerCase().includes(state.inputValue.toLowerCase())
          );
          return filtered;
        }}
        noOptionsText={
          <Typography sx={{ fontSize: "13px", color: "#9B9B9B" }}>{noOptionsText}</Typography>
        }
        sx={{
          "& .MuiAutocomplete-endAdornment": {
            top: "50% !important",
            transform: "translateY(-50%) !important",
          },
        }}
        slotProps={{
          paper: {
            sx: {
              mt: "4px",
              borderRadius: "8px",
              boxShadow: "0px 8px 24px rgba(0,0,0,0.10)",
              border: "1px solid #EBEBEB",
            },
          },
          listbox: {
            sx: {
              py: "4px",
              "& .MuiAutocomplete-option": {
                fontSize: "14px",
                color: "#1F2937",
                padding: "6px 12px",
                "&[aria-selected='true']": {
                  backgroundColor: "#F5F3FF",
                  color: "#4338CA",
                  fontWeight: 500,
                },
                "&.Mui-focused": {
                  backgroundColor: "#F9FAFB",
                },
                "&[aria-selected='true'].Mui-focused": {
                  backgroundColor: "#EDE9FE",
                },
              },
            },
          },
        }}
        renderOption={(props, option) => {
          if (option.id === SELECT_ALL_ID) {
            return (
              <React.Fragment key={SELECT_ALL_ID}>
                <li
                  {...props}
                  style={{ padding: "6px 12px", cursor: "pointer" }}
                  onClick={(e) => { e.preventDefault(); handleChange(null, [option]); }}
                >
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected}
                    sx={{ p: 0, mr: 1, color: "#4D44D8", "&.Mui-checked": { color: "#4D44D8" }, "&.MuiCheckbox-indeterminate": { color: "#4D44D8" } }}
                  />
                  <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#4D44D8" }}>
                    {allSelected ? "Deselect All" : "Select All"} ({options.length})
                  </Typography>
                </li>
                <Divider sx={{ my: "2px" }} />
              </React.Fragment>
            );
          }
          const isSelected = selectedIds.includes(String(option.id));
          return (
            <li {...props} key={option.id}>
              <Checkbox
                size="small"
                checked={isSelected}
                sx={{ p: 0, mr: 1, color: "#D0D5DD", "&.Mui-checked": { color: "#4D44D8" } }}
              />
              <Typography sx={{ fontSize: "13px" }}>{option.label}</Typography>
            </li>
          );
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                label={option.label}
                size="small"
                deleteIcon={<CloseRoundedIcon sx={{ fontSize: "13px !important" }} />}
                {...tagProps}
                sx={{
                  backgroundColor: "#EEF2FF",
                  border: "1px solid #C7D2FE",
                  color: "#4338CA",
                  fontWeight: 500,
                  fontSize: "12px",
                  height: "24px",
                  borderRadius: "4px",
                  "& .MuiChip-deleteIcon": {
                    color: "#6366F1",
                    "&:hover": { color: "#4338CA" },
                  },
                }}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={selectedOptions.length === 0 ? placeholder : ""}
            variant="outlined"
            size="small"
            onPaste={handlePaste}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: "13px",
                backgroundColor: "#FFFFFF",
                paddingRight: "65px !important",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D0D5DD",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#4D44D8",
                },
                "&.Mui-focused": {
                  boxShadow: "0 0 0 3px rgba(77,68,216,0.08)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#4D44D8",
                  borderWidth: "1px",
                },
                "& .MuiAutocomplete-input": {
                  fontSize: "13px",
                  color: "#1F2937",
                  minWidth: "60px !important",
                },
                "& .MuiAutocomplete-tag": {
                  margin: "2px",
                },
              },
            }}
          />
        )}
      />
    </Box>
  );
};

export default MultiSelect;
